import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { MultiSelect } from "react-multi-select-component";
import AdminSubdevisionService from "../../API/Services/AdminService/AdminSubdevisionService";

const LazySubdivisionMultiSelect = ({
  builderIds = [],
  value,
  onChange,
  placeholder = "Select Subdivision Name",
  className = "",
  name = "subdivision_name",
  isMulti = true,
  includeNoneOption = false,
  noneOptionLabel = "Select Subdivision None",
}) => {
  const [options, setOptions] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const inFlightKeyRef = useRef("");
  const optionsRef = useRef([]);
  const searchRef = useRef("");
  const lastSearchKeyRef = useRef("");
  const requestRef = useRef(0);
  const valueRef = useRef(value);
  const instanceClassName = `lazy-subdivision-multiselect-${useId().replace(/:/g, "")}`;
  const builderKey = JSON.stringify(builderIds);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  const formatSubdivisions = (subdivisionData) =>
    subdivisionData.map((subdivision) => ({
      label: subdivision.name,
      value: subdivision.id,
      masterplan_id: subdivision.masterplan_id,
    }));

  const mergeOptions = (prevOptions, nextOptions) => {
    const selectedOptions = Array.isArray(valueRef.current)
      ? valueRef.current
      : valueRef.current?.value !== undefined
        ? [valueRef.current]
        : [];
    const noneOptions = includeNoneOption
      ? [{ label: noneOptionLabel, value: "none", masterplan_id: null }]
      : [];

    const mergedOptions = [...noneOptions, ...selectedOptions, ...prevOptions, ...nextOptions];

    return mergedOptions.filter(
      (option, index, self) =>
        index === self.findIndex((item) => String(item.value) === String(option.value))
    );
  };

  const updateHasMore = useCallback((value) => {
    hasMoreRef.current = value;
    setHasMore(value);
  }, []);

  const fetchSubdivisions = useCallback(async (cursor = null, append = false, search = searchRef.current) => {
    if (append && (loadingRef.current || !hasMoreRef.current)) {
      return [];
    }

    const requestKey = `${builderKey}|${search}|${cursor || ""}|${append ? "append" : "replace"}`;

    if (inFlightKeyRef.current === requestKey) {
      return optionsRef.current;
    }

    const requestId = ++requestRef.current;
    inFlightKeyRef.current = requestKey;
    loadingRef.current = true;
    setLoading(true);

    try {
      const response = await AdminSubdevisionService.subdivisionbybuilderidlist({
        builder_ids: JSON.parse(builderKey),
        cursor,
        search,
      });
      const responseData = await response.json();
      const subdivisionData = Array.isArray(responseData) ? responseData : responseData.data || [];
      const formattedData = formatSubdivisions(subdivisionData);

      if (requestId !== requestRef.current) {
        return [];
      }

      const nextMergedOptions = append
        ? mergeOptions(optionsRef.current, formattedData)
        : mergeOptions([], formattedData);

      optionsRef.current = nextMergedOptions;
      setOptions(nextMergedOptions);
      lastSearchKeyRef.current = `${builderKey}|${search}`;
      setNextCursor(responseData.next_cursor || null);
      updateHasMore(Boolean(responseData.has_more));

      return formattedData;
    } catch (error) {
      if (error.name === "HTTPError") {
        await error.response.json();
      }

      return [];
    } finally {
      if (requestId === requestRef.current) {
        inFlightKeyRef.current = "";
        loadingRef.current = false;
        setLoading(false);
      }
    }
  }, [builderKey, updateHasMore, includeNoneOption, noneOptionLabel]);

  const handleFilterOptions = async (currentOptions, filter) => {
    const search = filter.trim();
    const searchKey = `${builderKey}|${search}`;

    if (searchKey === lastSearchKeyRef.current) {
      return optionsRef.current;
    }

    searchRef.current = search;
    setNextCursor(null);
    updateHasMore(true);

    return fetchSubdivisions(null, false, search);
  };

  useEffect(() => {
    setOptions([]);
    optionsRef.current = [];
    setNextCursor(null);
    updateHasMore(true);
    searchRef.current = "";
    lastSearchKeyRef.current = "";
    inFlightKeyRef.current = "";

    if (isOpen) {
      fetchSubdivisions(null, false);
    }
  }, [builderKey, fetchSubdivisions, isOpen, updateHasMore]);

  useEffect(() => {
    if (!isOpen) return;

    let menu = null;
    const timer = setTimeout(() => {
      menu = document.querySelector(`.${instanceClassName} .options`);

      if (!menu) return;

      const handleScroll = (event) => {
        const target = event.target;
        const bottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 40;

        if (bottom && nextCursor) {
          fetchSubdivisions(nextCursor, true, searchRef.current);
        }
      };

      menu.addEventListener("scroll", handleScroll);
      menu._lazySubdivisionScrollHandler = handleScroll;
    }, 100);

    return () => {
      clearTimeout(timer);

      if (menu && menu._lazySubdivisionScrollHandler) {
        menu.removeEventListener("scroll", menu._lazySubdivisionScrollHandler);
        delete menu._lazySubdivisionScrollHandler;
      }
    };
  }, [isOpen, nextCursor, hasMore, loading, builderKey, fetchSubdivisions, instanceClassName]);

  const selectedValue = isMulti
    ? Array.isArray(value) ? value : []
    : value?.value !== undefined ? [value] : [];

  const handleChange = (selectedItems) => {
    if (isMulti) {
      onChange(selectedItems);
      return;
    }

    onChange(selectedItems[selectedItems.length - 1] || null);
  };

  return (
    <>
      {!isMulti && (
        <style>
          {`
            .${instanceClassName} .options input[type="checkbox"] {
              display: none;
            }
          `}
        </style>
      )}
      <MultiSelect
        className={`lazy-subdivision-multiselect ${instanceClassName} ${className}`.trim()}
        name={name}
        options={options}
        value={selectedValue}
        onChange={handleChange}
        placeholder={placeholder}
        hasSelectAll={false}
        isLoading={loading}
        filterOptions={handleFilterOptions}
        debounceDuration={500}
        overrideStrings={{
          selectSomeItems: placeholder,
        }}
        onMenuToggle={(open) => {
          setIsOpen(open);
        }}
      />
    </>
  );
};

export default LazySubdivisionMultiSelect;
